import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Card, Button, Alert } from '../ui';
import projectService from '../../services/projectService';

const MigrateToForumModal = ({ projectId, project, onClose, onDone }) => {
  const [mode, setMode] = useState('create'); // 'create' | 'transfer'
  const [newProjectName, setNewProjectName] = useState(project?.name || '');
  const [categoryName, setCategoryName] = useState(project?.name || '');
  const [targetProjectId, setTargetProjectId] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'confirm' | 'done' | 'error'
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Load forum-eligible projects for transfer mode
  const forumProjectsQuery = useQuery(
    'forum-projects',
    () => projectService.getProjects(),
    { enabled: mode === 'transfer' }
  );
  const forumProjects = (forumProjectsQuery.data || []).filter(
    (p) => p.bot_token && p.report_group_id && p.id !== Number(projectId) && (
      p.is_forum === true ||
      p.provisioning_status === 'auto' ||
      (p.owner_account_id != null && p.invite_link)
    )
  );

  const migrateMutation = useMutation(
    (payload) => projectService.migrateToForum(projectId, payload),
    {
      onSuccess: (resp) => {
        if (resp.success) {
          setResult(resp.data);
          setStep('done');
        } else {
          setErrorMsg(resp.error || 'Migration failed');
          setStep('error');
        }
      },
      onError: (err) => {
        setErrorMsg(err?.response?.data?.error || err?.message || 'Network error');
        setStep('error');
      },
    }
  );

  const handleConfirm = () => {
    const payload = {
      mode,
      confirm: true,
    };
    if (mode === 'create') {
      payload.new_project_name = newProjectName.trim();
      payload.category_name = (categoryName.trim() || newProjectName.trim());
    } else {
      payload.target_project_id = Number(targetProjectId);
      payload.category_name = (categoryName.trim() || project?.name || '');
    }
    migrateMutation.mutate(payload);
  };

  const handleDryRun = () => {
    const payload = { mode, confirm: false };
    if (mode === 'create') {
      payload.new_project_name = newProjectName.trim();
      payload.category_name = (categoryName.trim() || newProjectName.trim());
    } else {
      payload.target_project_id = Number(targetProjectId);
      payload.category_name = (categoryName.trim() || project?.name || '');
    }
    migrateMutation.mutate(payload);
  };

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={step === 'done' ? onClose : undefined} />
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Move "{project?.name}" to Forum
            </h3>

            {step === 'form' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mode</label>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'create' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="mode"
                        checked={mode === 'create'}
                        onChange={() => setMode('create')}
                        className="mt-1 text-primary-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Create new forum</div>
                        <div className="text-xs text-gray-500">Creates a new bot + forum group + first topic, copies keywords from this project.</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'transfer' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="mode"
                        checked={mode === 'transfer'}
                        onChange={() => setMode('transfer')}
                        className="mt-1 text-primary-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Transfer to existing forum</div>
                        <div className="text-xs text-gray-500">Creates a new topic + keyword category inside an existing forum project. This project stays as-is.</div>
                      </div>
                    </label>
                  </div>
                </div>

                {mode === 'create' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New project name</label>
                      <input
                        type="text"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g. Zyn Indonesia"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First category / topic name</label>
                      <input
                        type="text"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Defaults to new project name"
                      />
                    </div>
                  </div>
                )}

                {mode === 'transfer' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target forum project</label>
                      <select
                        value={targetProjectId}
                        onChange={(e) => setTargetProjectId(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select a forum project...</option>
                        {forumProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} (id: {p.id})
                          </option>
                        ))}
                      </select>
                      {forumProjects.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">No forum projects found. Use "Create new forum" instead.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category / topic name</label>
                      <input
                        type="text"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Defaults to source project name"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 'done' && result && (
              <div className="space-y-3">
                <div className="rounded-md bg-green-50 border border-green-200 p-3">
                  <p className="text-sm text-green-800 font-medium">
                    {result.mode === 'create' ? 'Forum created!' : 'Category transferred!'}
                  </p>
                </div>
                {result.mode === 'create' && result.inviteLink && (
                  <div className="space-y-2">
                    <a
                      href={result.inviteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center text-sm rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-primary-700 hover:bg-primary-100"
                    >
                      Join new forum ({result.inviteLink})
                    </a>
                    <p className="text-xs text-gray-500 text-center">
                      New project: {result.newProject?.name} (id: {result.newProject?.id}) · Category: {result.categoryName}
                    </p>
                  </div>
                )}
                {result.mode === 'transfer' && (
                  <p className="text-xs text-gray-500 text-center">
                    Target: {result.targetProject?.name} · New topic #{result.reportTopicId} · Category: {result.categoryName}
                  </p>
                )}
                <p className="text-xs text-gray-500 text-center">
                  The old project "{project?.name}" is untouched. You can delete it manually.
                </p>
              </div>
            )}

            {step === 'error' && (
              <Alert variant="error" className="mb-4">
                {errorMsg}
              </Alert>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
            {step === 'done' ? (
              <Button type="button" variant="primary" onClick={() => { onDone(result); onClose(); }}>
                Done
              </Button>
            ) : step === 'error' ? (
              <>
                <Button type="button" variant="primary" onClick={() => setStep('form')}>Back</Button>
                <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleConfirm}
                  isLoading={migrateMutation.isLoading}
                  disabled={mode === 'transfer' && !targetProjectId}
                >
                  {mode === 'create' ? 'Create forum' : 'Transfer category'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrateToForumModal;
